/**
 * Recognises the appointment rejections that PRET-1845 and PRET-1837 define as *expected*
 * outcomes rather than failures, so the UI can show them beside the fields instead of behind
 * the alert modal.
 *
 * The alert stays the catch-all for the genuinely unexpected — a duplicate email, a request
 * that fell over. Routing an ordinary validation outcome through it makes the coordinator
 * dismiss a dialog before they can read what to change.
 *
 * Kept here rather than in each modal because the invite modal and the appointment modal both
 * classify the same rejections, and two copies of the code list drift.
 */

/** Error codes thrown by the backend's appointments service. */
export const APPOINTMENT_VALIDATION_CODES = [
  "APPOINTMENT_ON_RELEASE_DATE",
  "APPOINTMENT_DATE_OCCUPIED",
  "ENROLLMENT_HAS_NO_ALLOCATION",
] as const;

/**
 * Returns the message to show inline, or null when the caller should fall back to the alert.
 *
 * @param error a rejection from `transformAxiosError`, shaped `{ message, error: ErrorType }`
 * @param fallback used when the backend named a code but no message
 */
export const readAppointmentValidationError = (
  error: unknown,
  fallback: string
): string | null => {
  const envelope = (
    error as { error?: { errorCode?: string; errorMessage?: string } } | null
  )?.error;

  if (
    !envelope?.errorCode ||
    !(APPOINTMENT_VALIDATION_CODES as readonly string[]).includes(
      envelope.errorCode
    )
  ) {
    return null;
  }

  return envelope.errorMessage?.trim() || fallback;
};
